import { useMemo, useState } from "react";
import { format, isSameDay, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CalendarIcon, Check, ChevronRight, Clock, MessageSquare, PhoneCall,
} from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Calendar } from "@/shared/ui/calendar.jsx";
import { ScrollArea } from "@/shared/ui/scroll-area.jsx";
import { LoadingState, EmptyState } from "@/shared/ui/spinner.jsx";
import { cn } from "@/shared/lib/cn.js";
import { formatTime, onlyDigits } from "@/shared/lib/format.js";
import { LeadDrawer } from "@/features/leads/components/LeadDrawer.jsx";
import { useAgendaItems, useLeads, useToggleNoteDone } from "@/features/leads/use-leads.js";
import { NOTE_TYPES } from "@/features/leads/lead-constants.js";

/**
 * Agenda comercial.
 *
 * Os compromissos são as anotações de retorno e ligação criadas no CRM.
 * A tela antiga tinha um array `MOCK_TASKS` fixo no código com três
 * pacientes fictícios: o que o usuário anotava nunca aparecia aqui.
 */
export function AgendaPage() {
  const { data: compromissos = [], isLoading } = useAgendaItems();
  const { data: leads = [] } = useLeads();
  const concluir = useToggleNoteDone();

  const [dia, setDia] = useState(new Date());
  const [leadAberto, setLeadAberto] = useState(null);

  const doDia = useMemo(
    () => (dia ? compromissos.filter((c) => isSameDay(new Date(c.reminderAt), dia)) : []),
    [compromissos, dia],
  );

  /** Dias que têm compromisso — marcados em negrito no calendário. */
  const diasComTarefa = useMemo(
    () => compromissos.map((c) => startOfDay(new Date(c.reminderAt))),
    [compromissos],
  );

  if (isLoading) {
    return (
      <AppShell title="Agenda Comercial" subtitle="Gestão de retornos e compromissos com leads">
        <LoadingState message="Carregando compromissos..." />
      </AppShell>
    );
  }

  return (
    <AppShell title="Agenda Comercial" subtitle="Gestão de retornos e compromissos com leads">
      <div className="flex flex-col gap-6 p-6 lg:h-[calc(100vh-7.5rem)] lg:flex-row">
        {/* ------------------------------------------------- calendário */}
        <div className="flex flex-col gap-4">
          <Card className="p-4 shadow-sm">
            <Calendar
              mode="single"
              selected={dia}
              onSelect={setDia}
              modifiers={{ comTarefa: diasComTarefa }}
              modifiersClassNames={{
                comTarefa: "font-bold text-primary underline decoration-2 underline-offset-4",
              }}
            />
          </Card>

          <Card className="border-primary/20 bg-primary/5 p-4">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Clock className="h-4 w-4 text-primary" />
              Resumo do dia
            </h4>
            <dl className="space-y-2 text-xs">
              <Resumo rotulo="Total de tarefas" valor={doDia.length} />
              <Resumo rotulo="Ligações" valor={doDia.filter((t) => t.type === "ligacao").length} />
              <Resumo rotulo="Retornos" valor={doDia.filter((t) => t.type === "retorno").length} />
              <Resumo rotulo="Concluídas" valor={doDia.filter((t) => t.done).length} />
            </dl>
          </Card>
        </div>

        {/* ------------------------------------------------ lista do dia */}
        <div className="flex min-w-0 flex-1 flex-col">
          <Card className="flex flex-1 flex-col overflow-hidden shadow-sm">
            <div className="flex items-center justify-between border-b bg-muted/30 p-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <CalendarIcon className="h-5 w-5 text-primary" />
                <span className="first-letter:uppercase">
                  {dia ? format(dia, "EEEE, d 'de' MMMM", { locale: ptBR }) : "Selecione uma data"}
                </span>
              </h3>
              <Badge variant="outline">
                {doDia.length} {doDia.length === 1 ? "tarefa" : "tarefas"}
              </Badge>
            </div>

            <ScrollArea className="flex-1">
              <div className="space-y-4 p-4">
                {doDia.length === 0 ? (
                  <EmptyState
                    icon={CalendarIcon}
                    title="Nenhum agendamento para este dia."
                    description="As anotações de retorno feitas no CRM aparecem aqui."
                  />
                ) : (
                  doDia.map((tarefa) => (
                    <TaskCard
                      key={tarefa.id}
                      tarefa={tarefa}
                      onConcluir={() => concluir.mutate({ noteId: tarefa.id, done: !tarefa.done })}
                      onAbrirLead={() =>
                        setLeadAberto(leads.find((l) => l.id === tarefa.leadId) ?? null)
                      }
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>
      </div>

      <LeadDrawer lead={leadAberto} onClose={() => setLeadAberto(null)} />
    </AppShell>
  );
}

function TaskCard({ tarefa, onConcluir, onAbrirLead }) {
  const meta = NOTE_TYPES[tarefa.type] ?? NOTE_TYPES.observacao;

  return (
    <Card className={cn("group p-4 transition-colors hover:border-primary/50", tarefa.done && "opacity-60")}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={meta.tone}>
              <meta.icon className="mr-1 h-3 w-3" />
              {meta.label}
            </Badge>
            <span className="text-xs font-medium text-muted-foreground">
              {formatTime(tarefa.reminderAt)}
            </span>
            {tarefa.done && (
              <Badge variant="success" className="text-[10px]">
                Concluída
              </Badge>
            )}
          </div>

          <h4 className={cn("text-base font-bold transition-colors", tarefa.done && "line-through")}>
            {tarefa.leadName}
          </h4>
          <p className="mb-3 text-xs text-muted-foreground">{tarefa.leadProcedure || "—"}</p>

          <p className="rounded-lg border border-border/50 bg-muted/50 p-3 text-sm italic text-muted-foreground">
            &ldquo;{tarefa.text}&rdquo;
          </p>
        </div>

        <div className="flex shrink-0 flex-col gap-2">
          <Button
            size="icon"
            variant={tarefa.done ? "default" : "outline"}
            className="h-9 w-9 rounded-full"
            onClick={onConcluir}
            aria-label={tarefa.done ? "Reabrir tarefa" : "Marcar como concluída"}
            title={tarefa.done ? "Reabrir tarefa" : "Marcar como concluída"}
          >
            <Check className="h-4 w-4" />
          </Button>

          <Button
            asChild={Boolean(tarefa.leadPhone)}
            size="icon"
            variant="outline"
            className="h-9 w-9 rounded-full"
            aria-label="Ligar para o lead"
            title="Ligar"
            disabled={!tarefa.leadPhone}
          >
            {tarefa.leadPhone ? (
              <a href={`tel:${onlyDigits(tarefa.leadPhone)}`}>
                <PhoneCall className="h-4 w-4" />
              </a>
            ) : (
              <PhoneCall className="h-4 w-4" />
            )}
          </Button>

          <Button
            size="icon"
            variant="ghost"
            className="h-9 w-9 rounded-full"
            onClick={onAbrirLead}
            aria-label="Abrir conversa do lead"
            title="Abrir conversa"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>

          <Button
            size="icon"
            variant="ghost"
            className="h-9 w-9 rounded-full"
            onClick={onAbrirLead}
            aria-label="Abrir ficha do lead"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Resumo({ rotulo, valor }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{rotulo}:</dt>
      <dd className="font-medium">{valor}</dd>
    </div>
  );
}
